import React, { useEffect } from "react";
import DashboardProcurementChecklistSection from "./DashboardProcurementChecklistSection";

interface DashboardProcurementChecklistProps {
    data: any;
    project: any;
}

const DashboardProcurementChecklist: React.FC<DashboardProcurementChecklistProps> = (props) => {
    const [autoScroll, setAutoScroll] = React.useState(false);

    const containerRef = React.useRef<HTMLDivElement>(null);

    const checkOverflow = (el: HTMLDivElement) => {
        const curOverflow = el.style.overflow;

        if (!curOverflow || curOverflow === "visible") el.style.overflow = "hidden";

        const isOverflowing = el.clientHeight < el.scrollHeight;

        el.style.overflow = curOverflow;

        return isOverflowing;
    };

    const scrollInterval = React.useRef<NodeJS.Timeout | null>(null);

    const scrollElement = (el: HTMLElement) => {
        if (!el) return;

        const scrollAmount = 1;

        const scroll = () => {
            // If we are at the bottom of the element, scroll to the top
            // and start scrolling again after a delay
            if (el.scrollTop + el.clientHeight >= el.scrollHeight) {
                clearInterval(scrollInterval.current as NodeJS.Timeout);
                setTimeout(() => {
                    el.scrollTo({ top: 0, behavior: "smooth" });
                }, 1000);
                setTimeout(() => {
                    scrollInterval.current = setInterval(scroll, 100);
                }, 5000);
            } else {
                el.scrollBy({
                    top: scrollAmount,
                    behavior: "smooth",
                });
            }
        };

        scrollInterval.current = setInterval(scroll, 100);
        return () => clearInterval(scrollInterval.current as NodeJS.Timeout);
    };

    useEffect(() => {
        if (containerRef.current && autoScroll) {
            scrollElement(containerRef.current);
        }
        return () => {
            if (scrollInterval.current) {
                clearInterval(scrollInterval.current);
            }
        };
    }, [autoScroll]);

    useEffect(() => {
        if (containerRef.current) {
            setAutoScroll(checkOverflow(containerRef.current));
        }
    }, [containerRef]);

    return (
        <div
            id="procurement-checklist"
            ref={containerRef}
            className="flex flex-col h-full w-full gap-3 overflow-auto no-scrollbar scroll-smooth"
            onClick={() => {
                if (scrollInterval.current) {
                    clearInterval(scrollInterval.current);
                    scrollInterval.current = null;
                } else {
                    scrollElement(containerRef.current as HTMLElement);
                }
            }}
        >
            {props.data.needs_submit_by.length > 0 && (
                <DashboardProcurementChecklistSection
                    section="submit"
                    project={props.project}
                    items={props.data.needs_submit_by.sort((a, b) => (a.submitBy?.isBefore(b.submitBy) ? -1 : 1))}
                />
            )}
            {props.data.needs_approve_by.length > 0 && (
                <DashboardProcurementChecklistSection
                    section="approve"
                    project={props.project}
                    items={props.data.needs_approve_by.sort((a, b) => (a.approveBy?.isBefore(b.approveBy) ? -1 : 1))}
                />
            )}
            {props.data.needs_ordered.length > 0 && (
                <DashboardProcurementChecklistSection
                    section="order"
                    project={props.project}
                    items={props.data.needs_ordered.sort((a, b) => (a.orderBy?.isBefore(b.orderBy) ? -1 : 1))}
                />
            )}
            {props.data.needs_check_in_lead_time.length > 0 && (
                <DashboardProcurementChecklistSection
                    section="checkin"
                    project={props.project}
                    items={props.data.needs_check_in_lead_time}
                    sectionLabel="Check-Ins Due (unordered)"
                />
            )}
            {props.data.needs_check_in_aos.length > 0 && (
                <DashboardProcurementChecklistSection
                    section="checkin"
                    project={props.project}
                    items={props.data.needs_check_in_aos}
                    sectionLabel="Check-Ins Due (ordered)"
                />
            )}
            {props.data.needs_delivery.length > 0 && (
                <DashboardProcurementChecklistSection
                    section="receive"
                    project={props.project}
                    items={props.data.needs_delivery.sort((a, b) => (a.deliveryBy?.isBefore(b.deliveryBy) ? -1 : 1))}
                    sectionLabel="Schedule Jobsite Delivery"
                />
            )}
        </div>
    );
};

export default DashboardProcurementChecklist;
